(function () {
  'use strict';

  // ── Storage keys ────────────────────────────────────────────────────────
  const STORAGE_KEY  = 'pamuuc-consent-v3';
  const GROUP_KEY    = 'pamuuc-country-group';
  const CONSENT_DAYS = 30;

  // ── Tier 1 — EU + UK + EEA + Switzerland ───────────────────────────────
  // Tracking fires immediately. Banner is informational on first visit only.
  // Covers all 27 EU member states + EEA (NO, IS) + UK + Switzerland.
  const EU_COUNTRIES = [
    // EU member states (27)
    'AT','BE','BG','CY','CZ','DE','DK','EE','ES','FI','FR','GR','HR','HU',
    'IE','IT','LT','LU','LV','MT','NL','PL','PT','RO','SE','SI','SK',
    // EEA non-EU (Norway, Iceland)
    'NO','IS',
    // UK (UK GDPR — equivalent to EU GDPR post-Brexit)
    'GB',
    // Switzerland (adequacy decision)
    'CH'
  ];

  // ── Tier 2 — Consent whitelist ──────────────────────────────────────────
  // These countries see the consent banner and can allow tracking on click.
  // Everything not in this list AND not in EU → hard blocked (Tier 3).
  const CONSENT_COUNTRIES = [
    'US', // United States
    'CA'  // Canada
  ];

  // ── Country detection ───────────────────────────────────────────────────
  // Shopify's getRegion() returns ISO 3166-2 (e.g. "US-CA", "DE", "SG").
  // First two characters always give the ISO 3166-1 alpha-2 country code.
  // Works for both hyphenated ("US-CA") and compact ("USCA") formats.

  function getCountryCode(region) {
    if (!region || typeof region !== 'string') return null;
    const cc = region.slice(0, 2).toUpperCase();
    return cc.length === 2 ? cc : null;
  }

  function getCountryGroup(region) {
    const cc = getCountryCode(region);
    if (!cc) return 'CONSENT';                    // unknown country: give benefit of doubt
    if (EU_COUNTRIES.includes(cc))      return 'EU';
    if (CONSENT_COUNTRIES.includes(cc)) return 'CONSENT';
    return 'BLOCKED';                             // rest of world: hard deny
  }

  // ── Storage helpers ─────────────────────────────────────────────────────

  function hasStoredConsent() {
    try {
      const d = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (!d || !d.updated_at) return false;
      return (Date.now() - new Date(d.updated_at).getTime()) < CONSENT_DAYS * 864e5;
    } catch (e) { return false; }
  }

  function writeConsent(payload) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...payload,
        updated_at: new Date().toISOString()
      }));
    } catch (e) {}
  }

  function storeGroup(group) {
    try { localStorage.setItem(GROUP_KEY, group); } catch (e) {}
  }

  function hasBlockedDismissed() {
    try { return localStorage.getItem('pamuuc-blocked-dismissed') === '1'; } catch (e) { return false; }
  }

  function storeBlockedDismissed() {
    try { localStorage.setItem('pamuuc-blocked-dismissed', '1'); } catch (e) {}
  }

  // ── GA4 Consent Mode v2 ─────────────────────────────────────────────────

  function grantGtagConsent() {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        analytics_storage:       'granted',
        ad_storage:              'granted',
        ad_user_data:            'granted',
        ad_personalization:      'granted',
        functionality_storage:   'granted',
        personalization_storage: 'granted'
      });
    }
  }

  // ── DOM ─────────────────────────────────────────────────────────────────

  const root = document.querySelector('[data-cookie-consent]');
  if (!root) return;

  const banner        = root.querySelector('[data-cookie-banner]');
  const blockedBanner = root.querySelector('[data-cookie-blocked-banner]');
  const modal         = root.querySelector('[data-cookie-modal]');
  if (!banner || !blockedBanner || !modal) return;

  const acceptAllBtns     = root.querySelectorAll('[data-cookie-accept-all]');
  const manageBtn         = root.querySelector('[data-cookie-manage]');
  const saveSettingsBtn   = root.querySelector('[data-cookie-save-settings]');
  const modalCloseBtns    = root.querySelectorAll('[data-cookie-modal-close]');
  const blockedDismissBtn = root.querySelector('[data-cookie-blocked-dismiss]');

  // ── Shopify theme editor ────────────────────────────────────────────────
  // True inside the theme customizer preview. The banner never appears
  // there, and every consent side effect is suppressed.
  const DESIGN_MODE = !!(window.Shopify && window.Shopify.designMode);

  // ── State ───────────────────────────────────────────────────────────────

  const state = {
    customerPrivacy:  null,
    saleOfDataRegion: false,
    countryGroup:     null   // set once getRegion() resolves
  };

  // ── Visibility helpers ──────────────────────────────────────────────────

  const hideAll = () => {
    banner.hidden        = true;
    blockedBanner.hidden = true;
    modal.hidden         = true;
    document.documentElement.classList.remove('has-cookie-banner');
  };

  const showBanner = () => {
    banner.hidden        = false;
    blockedBanner.hidden = true;
    modal.hidden         = true;
    document.documentElement.classList.add('has-cookie-banner');
  };

  const showModal = () => {
    banner.hidden = true;
    modal.hidden  = false;
    const first = modal.querySelector('button, input');
    if (first) first.focus();
  };

  const hideModal = () => {
    modal.hidden  = true;
    banner.hidden = false;
  };

  const showBlockedBanner = () => {
    banner.hidden        = true;
    blockedBanner.hidden = false;
    modal.hidden         = true;
    document.documentElement.classList.add('has-cookie-banner');
  };

  // ── Full consent grant ──────────────────────────────────────────────────
  // Whatever the visitor clicks — Accept All, Save Settings, or the EU info
  // banner — we always grant everything. The UI choices are cosmetic.

  const fullPayload = () => {
    const p = { analytics: true, marketing: true, preferences: true };
    if (state.saleOfDataRegion) p.sale_of_data = true;
    return p;
  };

  const grantAll = () => {
    if (DESIGN_MODE) return;  // Editor preview — never store or grant anything.
    writeConsent(fullPayload());
    grantGtagConsent();
    if (state.customerPrivacy) {
      state.customerPrivacy.setTrackingConsent(fullPayload(), () => {});
    }
  };

  // ── Interaction handlers ────────────────────────────────────────────────

  // "Accept All" — grants everything and closes all UI.
  // BLOCKED visitors: only dismiss, no grant ever.
  const handleAcceptAll = () => {
    if (state.countryGroup === 'BLOCKED') { hideAll(); return; }
    grantAll();
    hideAll();
  };

  // "Manage Settings" — opens preference modal.
  const handleManage = () => showModal();

  // "Save Settings" — grants everything regardless of toggle state, closes modal.
  // BLOCKED visitors: only dismiss.
  const handleSaveSettings = () => {
    if (state.countryGroup === 'BLOCKED') { hideAll(); return; }
    grantAll();
    hideAll();
  };

  // Modal back / overlay click — return to banner without granting.
  const handleModalClose = () => hideModal();

  // ── Event listeners ─────────────────────────────────────────────────────

  acceptAllBtns.forEach(btn =>
    btn.addEventListener('click', e => { e.preventDefault(); handleAcceptAll(); })
  );

  if (manageBtn)
    manageBtn.addEventListener('click', e => { e.preventDefault(); handleManage(); });

  if (saveSettingsBtn)
    saveSettingsBtn.addEventListener('click', e => { e.preventDefault(); handleSaveSettings(); });

  modalCloseBtns.forEach(btn =>
    btn.addEventListener('click', handleModalClose)
  );

  if (blockedDismissBtn)
    blockedDismissBtn.addEventListener('click', () => { storeBlockedDismissed(); hideAll(); });

  // Close modal on Escape.
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && !modal.hidden) handleModalClose();
  });

  // Shopify fires this when their own regional UI collects consent.
  // Only act for CONSENT tier — EU manages its own grant/banner lifecycle
  // and BLOCKED never receives consent. Guarding against all other groups
  // prevents visitorConsentCollected (fired by our programmatic setTrackingConsent
  // for EU) from racing against showBanner() and hiding the informational notice.
  document.addEventListener('visitorConsentCollected', () => {
    if (state.countryGroup !== 'CONSENT') return;
    writeConsent({ analytics: true, marketing: true, preferences: true });
    grantGtagConsent();
    hideAll();
  });

  // ── Theme editor preview ────────────────────────────────────────────────
  // In the customizer the banner stays hidden entirely: no UI, no Customer
  // Privacy API call, and (via the grantAll guard) no consent side effects.
  if (DESIGN_MODE) {
    return;
  }

  // ── Shopify Customer Privacy API ────────────────────────────────────────

  if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
    // Shopify unavailable — fall back to standard consent banner.
    if (!hasStoredConsent()) showBanner();
    return;
  }

  window.Shopify.loadFeatures([
    { name: 'consent-tracking-api', version: '0.1' }
  ], (error) => {
    if (error) { if (!hasStoredConsent()) showBanner(); return; }

    state.customerPrivacy  = window.Shopify.customerPrivacy;
    state.saleOfDataRegion = state.customerPrivacy.saleOfDataRegion();

    const region         = state.customerPrivacy.getRegion();
    state.countryGroup   = getCountryGroup(region);
    storeGroup(state.countryGroup);   // Persists for next visit (read by init snippet)

    // ── TIER 1: EU + UK + EEA + Switzerland ──────────────────────────────
    if (state.countryGroup === 'EU') {
      const firstVisit = !hasStoredConsent();
      grantAll();  // Tracking on immediately — no waiting, no gate.
      if (firstVisit && state.customerPrivacy.shouldShowBanner()) {
        showBanner();  // Informational notice on first visit only.
      }
      return;
    }

    // ── TIER 3: Blocked countries ─────────────────────────────────────────
    if (state.countryGroup === 'BLOCKED') {
      window.PAMUUC_BLOCKED = true;  // Global flag — any other script can check.
      if (!hasBlockedDismissed()) showBlockedBanner();
      // No grantAll(). No grantGtagConsent(). Nothing fires. Ever.
      return;
    }

    // ── TIER 2: US + Canada ───────────────────────────────────────────────
    if (hasStoredConsent()) {
      // Returning visitor who already interacted — re-apply consent silently.
      grantAll();
      return;
    }
    // New visitor or expired consent — show banner and wait for interaction.
    if (state.customerPrivacy.shouldShowBanner()) {
      showBanner();
    }
  });
}());
